import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  OnInit,
  Inject,
  NgZone,
  Component,
  ViewChild,
  OnDestroy,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ISnPlayer } from '../../interfaces';

@Component({
  selector: 'sn-player-volume-control',
  templateUrl: './player-volume.control.html',
  styleUrls: ['./player-volume.control.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnPlayerVolumeControl implements OnInit, OnDestroy {
  public muted: boolean;

  @ViewChild('volumeSliderInput', { static: true })
  private readonly _volumeSliderInputElementRef: ElementRef<HTMLInputElement>;

  @ViewChild('unmuteButton', { static: true })
  private readonly _unmuteButtonRef: ElementRef<HTMLElement>;

  @ViewChild('muteButton', { static: true })
  private readonly _muteButtonRef: ElementRef<HTMLElement>;

  private readonly _volumeChangeHandlerFn = this._volumeChangeHandler.bind(this);
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _ngZone: NgZone,
    @Inject('SN_PLAYER') private readonly _player: ISnPlayer,
  ) {}

  public ngOnInit(): void {
    this._ngZone.runOutsideAngular(() => {
      this._initSubscriptions();
    });
  }

  public ngOnDestroy(): void {
    this._volumeSliderInputElementRef.nativeElement.removeEventListener(
      'change',
      this._volumeChangeHandlerFn,
    );

    this._destroy$.next();
    this._destroy$.complete();
  }

  public muteToggle(mute: boolean): void {
    this._player.muted = mute;
  }

  private _initSubscriptions(): void {
    this._player.volume$.pipe(takeUntil(this._destroy$)).subscribe((volume) => {
      this._volumeSliderInputElementRef.nativeElement.value = volume.toString();
      this._updateSlider(volume);
      this._updateMuteButton(volume);
    });

    this._volumeSliderInputElementRef.nativeElement.addEventListener(
      'change',
      this._volumeChangeHandlerFn,
    );
  }

  private _updateSlider(volume: number): void {
    const volumeSliderWidth = volume * 100;

    const volumeSliderInputElement = this._volumeSliderInputElementRef.nativeElement;
    volumeSliderInputElement.style.backgroundImage = `linear-gradient(
      to right,
      #fff 0%,
      #fff ${volumeSliderWidth}%,
      rgba(255, 255, 255, .2) ${volumeSliderWidth}%,
      rgba(255, 255, 255, .2) 100%
    )`;
  }

  private _updateMuteButton(volume: number): void {
    if (volume) {
      this._muteButtonRef.nativeElement.classList.remove('display-none');
      this._unmuteButtonRef.nativeElement.classList.add('display-none');
    } else {
      this._muteButtonRef.nativeElement.classList.add('display-none');
      this._unmuteButtonRef.nativeElement.classList.remove('display-none');
    }
  }

  private _volumeChangeHandler(): void {
    this._player.changeVolume(+this._volumeSliderInputElementRef.nativeElement.value);
  }
}
